import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    deleteTakeoffItem,
    updateTakeoffItem,
    restoreTakeoffItem,
    createTakeoffItem,
    aiAssistTakeoffItem,
} from "../../lib/api";

/**
 * Takeoff state machine — encapsulates every piece of state related to
 * AI Takeoff shapes (geometry, undo history, manual draw mode, AI
 * assist) so the WorkspacePage shell stays focused on layout +
 * orchestration. Extracted from WorkspacePage.jsx (iter-103 refactor).
 *
 * Returns a flat object with every state setter + handler the page
 * consumes. The consumer spread-destructures it at the top of the
 * component so the rest of the JSX stays untouched.
 *
 * Args:
 *   project      — current project object (needed for new-item POST)
 *   activeFile   — currently-viewed upload (target for manual draw)
 *   loadAll      — full reload helper used as a safety-net rollback
 *                  when an optimistic edit fails server-side.
 */
export function useTakeoffState({ project, activeFile, loadAll }) {
    const [takeoffItems, setTakeoffItems] = useState([]);
    const [takingOff, setTakingOff] = useState(false);
    const [selectedTakeoffId, setSelectedTakeoffId] = useState(null);
    // Undo stack — every drag-commit / reclassify / delete pushes an
    // inverse onto this stack BEFORE applying so the toolbar's Undo
    // button can revert via the canonical API (PATCH for drag /
    // reclassify, POST /restore for delete). Capped at 20 entries to
    // bound memory.
    const [takeoffHistory, setTakeoffHistory] = useState([]);
    const [undoing, setUndoing] = useState(false);
    // Manual draw mode — when non-null, clicks on the takeoff overlay
    // accumulate vertices instead of selecting shapes. `Finish` (or
    // double-click) commits via POST /takeoff/items. `Esc` cancels.
    const [drawMode, setDrawMode] = useState(null); // null | "area" | "linear" | "count"
    const [drawTrade, setDrawTrade] = useState("earthwork");
    const [drawPoints, setDrawPoints] = useState([]);
    const [drawSaving, setDrawSaving] = useState(false);
    const [aiAssistPendingId, setAiAssistPendingId] = useState(null);
    // iter-106: the PDF's natural viewport dimensions (PDF points) at
    // the moment the user is drawing. Stored so handleDrawFinish can
    // convert from PDF-point space → image-pixel space (the canonical
    // coordinate space AI items also use) before persisting.
    const [pdfPageSize, setPdfPageSize] = useState(null);

    const pushTakeoffHistory = (entry) => {
        setTakeoffHistory((prev) => {
            const next = [...prev, entry];
            return next.length > 20 ? next.slice(next.length - 20) : next;
        });
    };

    const takeoffItemsByUploadId = useMemo(() => {
        const by = {};
        for (const it of takeoffItems) {
            (by[it.upload_id] = by[it.upload_id] || []).push(it);
        }
        return by;
    }, [takeoffItems]);

    const handleDeleteTakeoffItem = async (id) => {
        // Snapshot the full doc BEFORE delete so undo can re-create it
        // exactly. We capture from local state — no extra round-trip.
        const snapshot = takeoffItems.find((x) => x.id === id);
        try {
            await deleteTakeoffItem(id);
            setTakeoffItems((prev) => prev.filter((x) => x.id !== id));
            if (selectedTakeoffId === id) setSelectedTakeoffId(null);
            if (snapshot) pushTakeoffHistory({ kind: "delete", item: snapshot });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't delete takeoff");
        }
    };

    // Drag-edit commit from TakeoffOverlay vertex handles. Server
    // recomputes the quantity using the shape's original pixel-per-foot
    // scale.
    const handleCommitTakeoffPoints = async (id, points) => {
        const before = takeoffItems.find((x) => x.id === id);
        setTakeoffItems((prev) =>
            prev.map((x) => (x.id === id ? { ...x, points } : x))
        );
        try {
            const updated = await updateTakeoffItem(id, { points });
            setTakeoffItems((prev) =>
                prev.map((x) => (x.id === id ? updated : x))
            );
            if (before) {
                pushTakeoffHistory({
                    kind: "patch",
                    id,
                    revertPatch: {
                        points: before.points,
                        quantity_sf: before.quantity_sf ?? null,
                        quantity_lf: before.quantity_lf ?? null,
                        quantity_each: before.quantity_each ?? null,
                    },
                });
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save edit");
            // Rollback on failure — full reload restores the canonical
            // server state.
            loadAll?.();
        }
    };

    const handleReclassifyTakeoffItem = async (id, nextType) => {
        const before = takeoffItems.find((x) => x.id === id);
        try {
            const updated = await updateTakeoffItem(id, { type: nextType });
            setTakeoffItems((prev) =>
                prev.map((x) => (x.id === id ? updated : x))
            );
            toast.success(`Reclassified as ${nextType}`);
            if (before) {
                pushTakeoffHistory({
                    kind: "patch",
                    id,
                    revertPatch: {
                        type: before.type,
                        quantity_sf: before.quantity_sf ?? null,
                        quantity_lf: before.quantity_lf ?? null,
                        quantity_each: before.quantity_each ?? null,
                    },
                });
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't reclassify");
        }
    };

    const handleUndoTakeoff = async () => {
        if (undoing || takeoffHistory.length === 0) return;
        const entry = takeoffHistory[takeoffHistory.length - 1];
        setUndoing(true);
        try {
            if (entry.kind === "delete" && entry.item) {
                const restored = await restoreTakeoffItem(entry.item);
                setTakeoffItems((prev) => {
                    const without = prev.filter((x) => x.id !== restored.id);
                    return [...without, restored];
                });
                toast.success("Restored takeoff item");
            } else if (entry.kind === "patch" && entry.id && entry.revertPatch) {
                const updated = await updateTakeoffItem(entry.id, entry.revertPatch);
                setTakeoffItems((prev) =>
                    prev.map((x) => (x.id === entry.id ? updated : x))
                );
                toast.success("Reverted last edit");
            }
            setTakeoffHistory((prev) => prev.slice(0, -1));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't undo");
        } finally {
            setUndoing(false);
        }
    };

    // Cmd/Ctrl+Z keyboard shortcut. Scoped to this hook's lifetime so
    // it doesn't hijack form-input undo elsewhere in the app.
    useEffect(() => {
        const onKey = (e) => {
            const meta = e.metaKey || e.ctrlKey;
            if (!meta || e.shiftKey || e.altKey) return;
            if (e.key !== "z" && e.key !== "Z") return;
            const t = e.target;
            const isFormField =
                t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
            if (isFormField) return;
            if (takeoffHistory.length === 0) return;
            e.preventDefault();
            handleUndoTakeoff();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [takeoffHistory, undoing]);

    // ----- Manual draw flow -----
    const handleDrawPoint = (xy) => {
        if (!drawMode) return;
        setDrawPoints((prev) => [...prev, xy]);
    };
    const handleDrawCancel = () => {
        setDrawMode(null);
        setDrawPoints([]);
    };
    const handleDrawFinish = async () => {
        if (!drawMode || drawSaving) return;
        const min = { area: 3, linear: 2, count: 1 }[drawMode];
        if (drawPoints.length < min) {
            toast.error(`Need at least ${min} point${min > 1 ? "s" : ""} to finish a ${drawMode}`);
            return;
        }
        if (!activeFile) {
            toast.error("Open a PDF page first to draw on it");
            return;
        }
        setDrawSaving(true);
        try {
            // iter-106 — coord-space fix.
            //
            // drawPoints are captured in the PDF's natural viewport
            // space (PDF points, e.g. 612×792 for US-letter). AI takeoff
            // items are saved in image-pixel space (the raster PNG the
            // model was shown, e.g. 2200×1700) — see backend takeoff.py
            // line 685. TakeoffOverlay uses ONE viewBox for ALL items
            // on a page, so mixing spaces displaces every shape.
            //
            // Convert manual points → image-pixel space and store with
            // page_width/page_height = image_width/image_height so the
            // manual + AI items live in the same frame and the existing
            // overlay renders them correctly at any zoom level.
            const targetW = activeFile.image_width || pdfPageSize?.width || 2000;
            const targetH = activeFile.image_height || pdfPageSize?.height || 1500;
            const srcW = pdfPageSize?.width || targetW;
            const srcH = pdfPageSize?.height || targetH;
            const sx = targetW / srcW;
            const sy = targetH / srcH;
            const transformedPoints = drawPoints.map(([x, y]) => [
                Math.round(x * sx),
                Math.round(y * sy),
            ]);
            const created = await createTakeoffItem({
                project_id: project.id,
                upload_id: activeFile.id,
                page_number: activeFile.page_number || 1,
                page_width: targetW,
                page_height: targetH,
                type: drawMode,
                trade: drawTrade,
                label: `${drawTrade.replace(/_/g, " ")} ${drawMode}`,
                points: transformedPoints,
            });
            setTakeoffItems((prev) => [...prev, created]);
            setSelectedTakeoffId(created.id);
            // History entry so Cmd-Z removes the just-drawn shape.
            pushTakeoffHistory({ kind: "delete", item: created });
            toast.success(`Drew ${drawMode}`);
            setDrawMode(null);
            setDrawPoints([]);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save shape");
        } finally {
            setDrawSaving(false);
        }
    };

    // Esc cancels the in-progress draw; Enter finishes it.
    useEffect(() => {
        if (!drawMode) return;
        const onKey = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                handleDrawCancel();
            } else if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleDrawFinish();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawMode, drawPoints, drawSaving]);

    // Planswift-style lightbulb visibility toggle. Hidden shapes stay
    // in MongoDB but are filtered out of canvas rendering + estimate
    // inputs until clicked again.
    const handleToggleTakeoffVisibility = async (item) => {
        const nextHidden = !item.hidden;
        setTakeoffItems((prev) =>
            prev.map((x) => (x.id === item.id ? { ...x, hidden: nextHidden } : x))
        );
        try {
            const updated = await updateTakeoffItem(item.id, { hidden: nextHidden });
            setTakeoffItems((prev) =>
                prev.map((x) => (x.id === item.id ? updated : x))
            );
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't toggle visibility");
            setTakeoffItems((prev) =>
                prev.map((x) => (x.id === item.id ? { ...x, hidden: !nextHidden } : x))
            );
        }
    };

    const handleAiAssistTakeoffItem = async (id) => {
        setAiAssistPendingId(id);
        try {
            const updated = await aiAssistTakeoffItem(id);
            setTakeoffItems((prev) =>
                prev.map((x) => (x.id === id ? updated : x))
            );
            toast.success("AI re-ran the shape");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI Assist failed");
        } finally {
            setAiAssistPendingId(null);
        }
    };

    return {
        // state
        takeoffItems, setTakeoffItems,
        takingOff, setTakingOff,
        selectedTakeoffId, setSelectedTakeoffId,
        takeoffHistory,
        undoing,
        drawMode, setDrawMode,
        drawTrade, setDrawTrade,
        drawPoints, setDrawPoints,
        drawSaving,
        aiAssistPendingId,
        // iter-106: callback used by PdfViewer to tell us the current
        // page's natural viewport size — needed at draw-save time.
        setPdfPageSize,
        // memos
        takeoffItemsByUploadId,
        // handlers
        pushTakeoffHistory,
        handleDeleteTakeoffItem,
        handleCommitTakeoffPoints,
        handleReclassifyTakeoffItem,
        handleUndoTakeoff,
        handleDrawPoint,
        handleDrawCancel,
        handleDrawFinish,
        handleToggleTakeoffVisibility,
        handleAiAssistTakeoffItem,
    };
}
